import { parseDate } from '@internationalized/date';
import { Color, Spacing } from '@persona-id/design-tokens';
import { capitalize } from 'lodash/fp';
import { useCallback, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import OutsideClickHandler from 'react-outside-click-handler';
import styled, { css } from 'styled-components';

import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { Z_INDICES } from 'dashboard/styles/variables';
import Card from 'design-system/components/Card';
import Calendar from 'design-system/components/DatePicker/Calendar/Calendar';
import DateField from 'design-system/components/DatePicker/DateField';
import Input, { InputGroup } from 'design-system/components/Input';
import { MenuItem, MenuList } from 'design-system/components/Menu';
import Popover from 'design-system/components/Popover';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import useToggle from 'lib/hooks/useToggle';
import { RelativeDate, TimePeriodType } from 'lib/interfaces/ui-components';
import { useFormSecurely } from 'lib/security/reactHookForm';

enum DateRangeType {
  NotSet = 'not set',
  Absolute = 'absolute',
  Relative = 'relative',
  Today = 'today',
}
enum PastOrFutureType {
  Future = 'after today',
  Past = 'before today',
}

const PopoverCard = styled(Card)`
  width: 450px;
`;
const PresetsContainer = styled(Card.Body)`
  border-right: 1px solid ${Color.BorderHairline};
  width: 152px;
`;

const InputDateContainer = styled.div`
  display: flex;
`;

const StyledPresetButton = styled(MenuItem)<{ isSelected?: boolean }>`
  ${({ isSelected }) =>
    isSelected
      ? css`
          background-color: ${({ theme }) => theme.color.background.highlight};
          color: ${({ theme }) => theme.color.text.brand};
        `
      : css`
          &${isNotDisabled}${isFocused} {
            background-color: transparent;
            color: ${({ theme }) => theme.color.text.primary};
          }
          &${isNotDisabled}${isHovered} {
            background-color: transparent;
          }
        `}
`;
// Returns type of date range depending on if certain config values are set
const getDateRangeType = (absoluteDate: string | null, relativeDate: RelativeDate | null) => {
  if (absoluteDate == null && relativeDate == null) {
    return DateRangeType.NotSet;
  }
  if (absoluteDate == null) {
    if (relativeDate && relativeDate['quantity'] === 0) {
      return DateRangeType.Today;
    }
    return DateRangeType.Relative;
  }
  return DateRangeType.Absolute;
};

// Returns if relative date is in the past or future
const getPastOrFutureRelativeDateType = (relativeDate: RelativeDate | null) => {
  if (relativeDate && relativeDate.quantity > 0) {
    return PastOrFutureType.Future;
  }
  return PastOrFutureType.Past;
};

interface InputDatePickerPopupProps {
  onClose: (absoluteValue: string | null, relativeValue: RelativeDate | null) => void;
  absoluteDate: string | null;
  relativeDate: RelativeDate | null;
}

const InputDatePickerPopup = ({
  absoluteDate,
  relativeDate,
  onClose,
}: InputDatePickerPopupProps) => {
  const { control, setValue, getValues, reset } = useFormSecurely({
    defaultValues: { 'absolute-date': absoluteDate, 'relative-date': relativeDate },
  });

  const handleChangeRelativeDateType = (value: DateRangeType) => {
    setValue('relative-date', { quantity: 0, 'time-period': TimePeriodType.Days });
    setRelativeDateInPastOrFuture(PastOrFutureType.Past);
    setDateType(value);
  };

  const [dateType, setDateType] = useState<DateRangeType>(
    getDateRangeType(absoluteDate, relativeDate),
  );
  const [relativeDateInPastOrFuture, setRelativeDateInPastOrFuture] = useState<PastOrFutureType>(
    getPastOrFutureRelativeDateType(relativeDate),
  );

  useEffect(() => {
    reset({ 'absolute-date': absoluteDate, 'relative-date': relativeDate });
    setDateType(getDateRangeType(absoluteDate, relativeDate));
    setRelativeDateInPastOrFuture(getPastOrFutureRelativeDateType(relativeDate));
  }, [absoluteDate, relativeDate, reset]);
  return (
    <OutsideClickHandler
      onOutsideClick={() => onClose(getValues('absolute-date'), getValues('relative-date'))}
    >
      <PopoverCard elevated>
        <InputDateContainer>
          <PresetsContainer padding={Spacing[3]}>
            <SectionColumn>
              <MenuList>
                <StyledPresetButton
                  id='input-date-select-absolute'
                  aria-label='input-date-select-absolute'
                  isSelected={
                    dateType === DateRangeType.Absolute || dateType === DateRangeType.NotSet
                  }
                  onClick={() => {
                    setValue(`relative-date`, null);
                    setDateType(DateRangeType.Absolute);
                  }}
                >
                  Fixed
                </StyledPresetButton>
                <StyledPresetButton
                  id='input-date-select-relative'
                  aria-label='input-date-select-relative'
                  isSelected={
                    dateType === DateRangeType.Relative || dateType === DateRangeType.Today
                  }
                  onClick={() => {
                    setValue(`absolute-date`, null);
                    setDateType(DateRangeType.Today);
                    setValue(`relative-date`, {
                      quantity: 0,
                      'time-period': TimePeriodType.Days,
                    });
                  }}
                >
                  Relative
                </StyledPresetButton>
              </MenuList>
            </SectionColumn>
          </PresetsContainer>
          <Card.Body style={{ width: '100%' }}>
            {(dateType === DateRangeType.NotSet || dateType === DateRangeType.Absolute) && (
              <Controller
                name='absolute-date'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    <DateField
                      aria-label='Select an absolute date'
                      value={value ? parseDate(value) : undefined}
                      onChange={(dateValue) => {
                        onChange(dateValue?.toString());
                      }}
                    />
                    <Calendar
                      id='date-range-calendar'
                      // Setting focusedValue allows calendar to "flip" to appropriate month to display when value changes
                      focusedValue={value ? parseDate(value) : undefined}
                      onFocusChange={(dateValue) => {
                        onChange(dateValue?.toString());
                      }}
                      onChange={(dateValue) => {
                        onChange(dateValue?.toString());
                      }}
                      value={value ? parseDate(value) : null}
                    />
                  </>
                )}
              />
            )}
            {(dateType === DateRangeType.Relative || dateType === DateRangeType.Today) && (
              <>
                <RadioGroup
                  id='input-date-relative-date-picker-radio-group'
                  value={dateType}
                  onChange={handleChangeRelativeDateType}
                >
                  <Radio label='Today' value={DateRangeType.Today} />
                  <Radio label='Custom' value={DateRangeType.Relative} />
                </RadioGroup>
                {dateType === DateRangeType.Relative && (
                  <Controller
                    name='relative-date'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <SectionRow>
                          <Input
                            type='number'
                            size='sm'
                            id='form-section-input-date__relative-date-quantity'
                            placeholder='0'
                            value={value && value.quantity ? Math.abs(value.quantity) : ''}
                            onChange={(e) => {
                              let quantity = Math.abs(parseInt(e.target.value));
                              if (
                                !isNaN(quantity) &&
                                relativeDateInPastOrFuture === PastOrFutureType.Past
                              ) {
                                quantity = quantity * -1;
                              }
                              const updateRelativeDate = {
                                quantity: quantity,
                                'time-period': value ? value['time-period'] : TimePeriodType.Days,
                              };
                              onChange(updateRelativeDate);
                            }}
                          />
                          <Select
                            id='form-section-input-date__relative-date-time_period'
                            size='sm'
                            placeholder={capitalize(TimePeriodType.Days)}
                            value={value ? value['time-period'] : TimePeriodType.Days}
                            onValueChange={(timePeriod: TimePeriodType) => {
                              onChange({
                                quantity: value ? value.quantity : null,
                                'time-period': timePeriod,
                              });
                            }}
                          >
                            {Object.values(TimePeriodType).map((key: TimePeriodType) => (
                              <Option key={key} value={key}>
                                {capitalize(key)}
                              </Option>
                            ))}
                          </Select>
                        </SectionRow>
                        <Select
                          id='form-section-input-date__relative-date-past-or-future'
                          size='sm'
                          placeholder={capitalize(PastOrFutureType.Past)}
                          value={relativeDateInPastOrFuture}
                          onValueChange={(pastOrFutureType) => {
                            setRelativeDateInPastOrFuture(pastOrFutureType as PastOrFutureType);
                            if (value && value.quantity && value.quantity !== 0) {
                              const newQuantity =
                                pastOrFutureType === PastOrFutureType.Past
                                  ? -1 * Math.abs(value.quantity)
                                  : Math.abs(value.quantity);
                              onChange({
                                quantity: newQuantity,
                                'time-period': value ? value['time-period'] : TimePeriodType.Days,
                              });
                            }
                          }}
                        >
                          {Object.values(PastOrFutureType).map((key: string) => (
                            <Option key={key} value={key}>
                              {capitalize(key)}
                            </Option>
                          ))}
                        </Select>
                      </>
                    )}
                  />
                )}
              </>
            )}
          </Card.Body>
          <Spacer height={1} />
        </InputDateContainer>
      </PopoverCard>
    </OutsideClickHandler>
  );
};

interface InputDatePickerProps {
  onDateChange: (absoluteValue: string | null, relativeValue: RelativeDate | null) => void;
  absoluteDate: string | null;
  relativeDate: RelativeDate | null;
  disabled?: boolean;
}

const InputDatePicker = ({
  onDateChange,
  absoluteDate,
  relativeDate,
  disabled,
}: InputDatePickerProps) => {
  const [editingDate, { enable: showPopup, disable: hidePopup }] = useToggle(false);

  // Returns message to show current date range state
  const getDateRangeMessage = () => {
    const currentDateRangeType = getDateRangeType(absoluteDate, relativeDate);
    switch (currentDateRangeType) {
      case DateRangeType.NotSet:
        return 'Select date';
      case DateRangeType.Absolute:
        return absoluteDate;
      case DateRangeType.Today:
        return 'Today';
      case DateRangeType.Relative:
        return relativeDate
          ? `${Math.abs(relativeDate['quantity'])} ${
              relativeDate['time-period']
            } ${getPastOrFutureRelativeDateType(relativeDate)}`
          : 'Select date';
    }
  };

  const handleClose = useCallback(
    (absoluteDate: string | null, relativeDate: RelativeDate | null) => {
      onDateChange(absoluteDate, relativeDate);
      hidePopup();
    },
    [hidePopup, onDateChange],
  );

  const handleClear = useCallback(() => {
    onDateChange(null, null);
    hidePopup();
  }, [onDateChange, hidePopup]);

  return (
    <SectionRow>
      <Popover portal={true} open={editingDate}>
        <Popover.Trigger style={{ width: '100%' }}>
          <InputGroup onClear={absoluteDate || relativeDate ? handleClear : undefined}>
            <Input
              value={getDateRangeMessage() || ''}
              onFocus={showPopup}
              placeholder='Select date'
              disabled={disabled}
            />
          </InputGroup>
        </Popover.Trigger>

        <Popover.Content style={{ zIndex: Z_INDICES.modalElevated }} side='bottom' align='end'>
          <InputDatePickerPopup
            absoluteDate={absoluteDate}
            relativeDate={relativeDate}
            onClose={handleClose}
          />
        </Popover.Content>
      </Popover>
    </SectionRow>
  );
};

export default InputDatePicker;
